import { supabase } from "@/utils/supabase/client";

/** Legacy tickets / non-merch — still expects client amount. */
const MPESA_AUTH_URL =
    process.env.REACT_APP_MPESA_AUTH_URL ||
    "https://cheppar.co.ke/cheppar/authPay.php";

/** Merch only — server resolves amount; never fall back to authPay.php. */
function defaultMerchMpesaUrl() {
    if (process.env.REACT_APP_MPESA_MERCH_URL) {
        return process.env.REACT_APP_MPESA_MERCH_URL;
    }
    if (typeof window !== "undefined" && window.location?.origin) {
        return `${window.location.origin}/cheppar/authPayMerch.php`;
    }
    return "https://cheppar.co.ke/cheppar/authPayMerch.php";
}

const MERCH_MPESA_URL = defaultMerchMpesaUrl();

const KENYAN_PHONE_REGEX = /^(?:\+254|254|0)7\d{8}$/;

/** Validate Kenyan mobile (07… or +2547…). */
export function isValidKenyanPhone(phone) {
    const normalized = phone.replace(/\s/g, "");
    return KENYAN_PHONE_REGEX.test(normalized);
}

/** Format for authPay.php — +2547XXXXXXXX */
export function formatPhoneForMpesa(phoneNumber) {
    const trimmed = phoneNumber.replace(/\s/g, "");
    if (trimmed.startsWith("0")) {
        return `+254${trimmed.slice(1)}`;
    }
    if (trimmed.startsWith("254") && !trimmed.startsWith("+")) {
        return `+${trimmed}`;
    }
    return trimmed;
}

export function buildMerchInvoice() {
    return `MERCH-${Date.now()}`;
}

/**
 * STK push for merch — amount is resolved server-side (do not send amount).
 */
export async function initiateMerchMpesa({ phoneNumber, externalReference }) {
    const paymentData = {
        phone_number: formatPhoneForMpesa(phoneNumber),
        external_reference: externalReference,
    };

    const response = await fetch(MERCH_MPESA_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-BuildBout-Client": "buildbout",
        },
        body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
            `Payment request failed (${response.status}): ${errorText || "Unknown error"}`
        );
    }

    const responseData = await response.json();

    if (!responseData.success || responseData.status !== "QUEUED") {
        throw new Error(
            responseData.message || "Payment initiation failed."
        );
    }

    return responseData;
}

/**
 * STK push via Cheppar authPay.php (tickets / legacy — amount required).
 */
export async function initiateChepparMpesa({ phoneNumber, amount, externalReference }) {
    const paymentData = {
        phone_number: formatPhoneForMpesa(phoneNumber),
        amount,
        external_reference: externalReference,
    };

    const response = await fetch(MPESA_AUTH_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-BuildBout-Client": "buildbout",
        },
        body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
            `Payment request failed (${response.status}): ${errorText || "Unknown error"}`
        );
    }

    const responseData = await response.json();

    if (!responseData.success || responseData.status !== "QUEUED") {
        throw new Error(
            responseData.message || "Payment initiation failed."
        );
    }

    return responseData;
}

/**
 * Poll `gaspayments` until status is true (matches Reserve flow).
 */
export function pollGasPaymentStatus(
    userReference,
    {
        onSuccess,
        onError,
        onTimeout,
        retryCount = 15,
        intervalMs = 10000,
    } = {}
) {
    if (!supabase) {
        onError?.(new Error("Supabase is not configured for payment polling."));
        return () => {};
    }

    let cancelled = false;
    let attemptsLeft = retryCount;

    const tick = async () => {
        if (cancelled) return;

        if (attemptsLeft <= 0) {
            onTimeout?.();
            return;
        }

        try {
            const { data: paymentData, error: paymentError } = await supabase
                .from("gaspayments")
                .select("status, mpesa_reference")
                .eq("user_reference", userReference)
                .maybeSingle();

            if (paymentError || !paymentData) {
                attemptsLeft -= 1;
                if (!cancelled) {
                    window.setTimeout(tick, intervalMs);
                }
                return;
            }

            if (paymentData.status === true) {
                onSuccess?.(paymentData.mpesa_reference);
                return;
            }

            attemptsLeft -= 1;
            if (!cancelled) {
                window.setTimeout(tick, intervalMs);
            }
        } catch (err) {
            onError?.(err);
        }
    };

    window.setTimeout(tick, 5000);

    return () => {
        cancelled = true;
    };
}
